import { z } from 'zod';

export const APPLICANT_STAGES = ['referred', 'application_started', 'application_complete', 'decision_released', 'enrolled'] as const;
export const ApplicantStage = z.enum(APPLICANT_STAGES);
export type ApplicantStage = z.infer<typeof ApplicantStage>;

export const APPLICANT_STAGE_LABELS: Record<ApplicantStage, string> = {
  referred: 'Referred',
  application_started: 'Application started',
  application_complete: 'Application complete',
  decision_released: 'Decision released',
  enrolled: 'Enrolled',
};

export const UNMAPPED_APPLICANT_STAGE: ApplicantStage = 'referred';
export const MAX_STAGE_MAPPINGS = 200;
export const MAX_ADMISSION_STATUS_LENGTH = 100;

export const StageMappingEntry = z.object({
  status: z
    .string()
    .trim()
    .min(1, 'Veracross status is required')
    .max(MAX_ADMISSION_STATUS_LENGTH, `Veracross status must be at most ${MAX_ADMISSION_STATUS_LENGTH} characters`),
  stage: ApplicantStage,
});
export type StageMappingEntry = z.infer<typeof StageMappingEntry>;

export const admissionStatusKey = (status: string) => status.trim().toLowerCase();

export const StageMapping = z
  .array(StageMappingEntry)
  .max(MAX_STAGE_MAPPINGS, `At most ${MAX_STAGE_MAPPINGS} mapped statuses`)
  .superRefine((entries, ctx) => {
    const seen = new Set<string>();
    entries.forEach((e, i) => {
      const key = admissionStatusKey(e.status);
      if (seen.has(key)) ctx.addIssue({ code: 'custom', path: [i, 'status'], message: `Status "${e.status}" is mapped twice` });
      seen.add(key);
    });
  });
export type StageMapping = z.infer<typeof StageMapping>;

export const DEFAULT_STAGE_MAPPING: StageMapping = [
  { status: 'In-Process', stage: 'application_started' },
  { status: 'Submitted', stage: 'application_complete' },
  { status: 'Pending Review', stage: 'application_complete' },
  { status: 'In Review', stage: 'application_complete' },
  { status: 'Complete', stage: 'application_complete' },
];

export type StageLookup = ReadonlyMap<string, ApplicantStage>;

export function stageLookup(mapping: readonly StageMappingEntry[]): StageLookup {
  return new Map(mapping.map((e) => [admissionStatusKey(e.status), e.stage]));
}

export function applicantStage(admissionStatus: string | null | undefined, lookup: StageLookup): ApplicantStage {
  if (!admissionStatus?.trim()) return UNMAPPED_APPLICANT_STAGE;
  return lookup.get(admissionStatusKey(admissionStatus)) ?? UNMAPPED_APPLICANT_STAGE;
}

export function unmappedAdmissionStatuses(statuses: Iterable<string | null | undefined>, mapping: readonly StageMappingEntry[]): string[] {
  const lookup = stageLookup(mapping);
  const out = new Map<string, string>();
  for (const s of statuses) {
    const trimmed = s?.trim();
    if (!trimmed) continue;
    const key = admissionStatusKey(trimmed);
    if (!lookup.has(key) && !out.has(key)) out.set(key, trimmed);
  }
  return [...out.values()].sort((a, b) => a.localeCompare(b));
}
